/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import moment from 'moment';
import { ObservableModel } from '@typescript-kit/core';

export interface TimeClockShellViewData {
  timeClockId?: string;
  serverVersion: string;
  utcOffset?: number;
  timeOffset?: number;
  time?: string;
  nfcKey?: string;
}

export class TimeClockShellViewFieldKey {
  static readonly TIME_CLOCK_ID = 'timeClockId';
  static readonly SERVER_VERSION = 'serverVersion';
  static readonly UTC_OFFSET = 'utcOffset';
  static readonly TIME_OFFSET = 'timeOffset';
  static readonly TIME = 'time';
  static readonly NFC_KEY = 'nfcKey';
}

export class TimeClockShellViewModel extends ObservableModel {
  static readonly KEYS = Object.keys(TimeClockShellViewFieldKey).map((key) => TimeClockShellViewFieldKey[key]);

  constructor(data?: TimeClockShellViewData) {
    super(data, TimeClockShellViewModel.KEYS);
  }

  get timeClockId(): string {
    return this.get(TimeClockShellViewFieldKey.TIME_CLOCK_ID);
  }

  set timeClockId(value: string) {
    this.set(TimeClockShellViewFieldKey.TIME_CLOCK_ID, value);
  }

  get serverVersion(): string {
    return this.get(TimeClockShellViewFieldKey.SERVER_VERSION);
  }

  set serverVersion(value: string) {
    this.set(TimeClockShellViewFieldKey.SERVER_VERSION, value);
  }

  get utcOffset(): number {
    return this.get(TimeClockShellViewFieldKey.UTC_OFFSET);
  }

  set utcOffset(value: number) {
    this.set(TimeClockShellViewFieldKey.UTC_OFFSET, value);
  }

  get timeOffset(): number {
    return this.get(TimeClockShellViewFieldKey.TIME_OFFSET);
  }

  set timeOffset(value: number) {
    this.set(TimeClockShellViewFieldKey.TIME_OFFSET, value);
  }

  get time(): string {
    return this.get(TimeClockShellViewFieldKey.TIME);
  }

  set time(value: string) {
    this.set(TimeClockShellViewFieldKey.TIME, value);
  }

  get nfcKey(): string {
    return this.get(TimeClockShellViewFieldKey.NFC_KEY);
  }

  set nfcKey(value: string) {
    this.set(TimeClockShellViewFieldKey.NFC_KEY, value);
  }

  protected setupValues(values: TimeClockShellViewData, exclusive: boolean): TimeClockShellViewData {
    if (exclusive && !(TimeClockShellViewFieldKey.UTC_OFFSET in values)) {
      values.utcOffset = 0;
    }
    if (exclusive && !(TimeClockShellViewFieldKey.TIME_OFFSET in values)) {
      values.timeOffset = 0;
    }
    if (exclusive && !(TimeClockShellViewFieldKey.TIME in values)) {
      const timeOffset: number = this.getSetupValue(values, TimeClockShellViewFieldKey.TIME_OFFSET, exclusive);
      const now = moment(Date.now() + timeOffset);
      const utcOffset: number = this.getSetupValue(values, TimeClockShellViewFieldKey.UTC_OFFSET, exclusive);
      if (utcOffset) {
        now.utcOffset(utcOffset);
      }
      values.time = now.toISOString(true);
    }
    return super.setupValues(values, exclusive) as TimeClockShellViewData;
  }
}
